import moment from 'moment';
import { writeFile, utils } from 'xlsx-js-style';
import db from '@utils/db';

// Faithful port of UnmanagedApi's Export_Student.ts (excel4node, server-side) to xlsx-js-style
// (browser-side), following the same db-calling-utility pattern as pdfTools.ts's GeneratePdf -
// the middle layer no longer builds this file; the data source (GetReportData_LUDUS) is fetched
// directly and role-checked, and the workbook is built here.

const styleNormal = { font: { color: { rgb: '000000' }, sz: 10 } };
const styleRight = { font: { color: { rgb: '000000' }, sz: 10 }, alignment: { horizontal: 'right' } };
const styleBold = { font: { color: { rgb: '000000' }, sz: 14, bold: true } };

function setString(ws: any, row: number, col: number, value: string, style: any) {
  ws[utils.encode_cell({ r: row, c: col })] = { t: 's', v: value ?? '', s: style };
}

function setNumber(ws: any, row: number, col: number, value: number, style: any) {
  ws[utils.encode_cell({ r: row, c: col })] = { t: 'n', v: value, s: style };
}

function pad2(n: number): string {
  return n.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
}

export interface StudentExportText {
  filename: string;
  tabname: string;
  col1: string;
  col2: string;
  col3: string;
  col4: string;
  col5: string;
  col6: string;
  col7: string;
  col8: string;
  col9: string;
  skole: string;
  periode: string;
  tags: { tag_id: number; name: string }[];
}

export async function ExportStudentExcel(studentId: string, fromDate: moment.Moment, toDate: moment.Moment, exportText: StudentExportText) {
  if (!studentId) return Promise.reject('No studentId provided');
  const data = await db.GetReportData_LUDUS(studentId, fromDate, toDate);
  if (!data) return;
  generateStudentExcelReport(exportText, data);
}

function generateStudentExcelReport(exportText: StudentExportText, data: any[]) {
  const ws: any = {};

  setString(ws, 0, 0, exportText.col7, styleBold);
  setString(ws, 0, 1, exportText.col8, styleBold);
  setString(ws, 0, 2, exportText.col9, styleBold);
  setString(ws, 1, 0, exportText.tabname.replaceAll('_', ' '), styleNormal);
  setString(ws, 1, 1, exportText.skole, styleNormal);
  setString(ws, 1, 2, exportText.periode, styleNormal);

  setString(ws, 5, 0, exportText.col1, styleBold);
  setString(ws, 5, 1, exportText.col2, styleBold);
  setString(ws, 5, 2, exportText.col3, styleBold);
  setString(ws, 5, 3, exportText.col4, styleBold);
  setString(ws, 5, 4, exportText.col5, styleBold);
  setString(ws, 5, 5, exportText.col6, styleBold);

  for (let i = 0; i < (data?.length ?? 0); i++) {
    const row = data[i];
    const rowIndex = i + 6;

    const startTime = pad2(row.lessonstarttime.hours) + ':' + pad2(row.lessonstarttime.minutes);
    const endTime = pad2(row.lessonsendtime.hours) + ':' + pad2(row.lessonsendtime.minutes);

    const dateOnly = row.lessondate.split('T')[0];
    const duration = moment(dateOnly + ' ' + endTime).diff(dateOnly + ' ' + startTime, 'minutes');
    const absenceDuration = row.duration / 60;

    let absence = 0;
    if (row.absencetype === 2) absence = duration;
    else if (row.absencetype === 3) absence = absenceDuration;
    else if (row.absencetype === 1 || row.absencetype === -1) absence = 0;
    else absence = 15;

    let cause = '';
    if (row.absencetype !== 1 && row.absencetype !== -1) {
      const tagIds: string[] = row.tag_ids ? row.tag_ids.split(',') : [];
      tagIds.forEach((tagId) => {
        const tag = exportText.tags.find((t) => t.tag_id == (tagId as any));
        if (tag) {
          if (cause !== '') cause += ', ';
          cause += tag.name;
        }
      });
    }

    setString(ws, rowIndex, 0, moment(row.lessondate).format('D MMM YYYY'), styleRight);
    setString(ws, rowIndex, 1, startTime + ' - ' + endTime, styleNormal);
    setString(ws, rowIndex, 2, row.course_Name, styleNormal);
    setString(ws, rowIndex, 3, row.course_Displayname, styleNormal);
    setString(ws, rowIndex, 4, cause, styleNormal);
    setNumber(ws, rowIndex, 5, absence, styleNormal);
  }

  const lastRow = 6 + (data?.length ?? 0);
  ws['!ref'] = `A1:F${Math.max(lastRow, 1)}`;
  ws['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 20 }];

  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, ws, exportText.tabname.substr(0, 31));
  writeFile(workbook, exportText.filename + '.xlsx');
}
